import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styled, { useTheme } from 'styled-components'

import { FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout'
import { theme } from '@/theme-generated'
import { type Message, MessageReadStatus, type MessageType } from '../../types/message.types'

import type { ReactNode } from 'react'

const messageIcons: Record<MessageType, ReactNode> = {
  Message: <FontAwesomeIcon icon={['fal', 'info']} color={theme.Colors.Neutrals.Darkest} />,
  PatientCommunication: <FontAwesomeIcon icon={['fal', 'message-dots']} color={theme.Colors.Neutrals.Darkest} />,
}

function stripHTML(html?: string) {
  return html?.replace(/<[^>]*>?/g, '')
}

export const MessageItem = ({ message, onClick }: { message: Message; onClick?: () => void }) => {
  const theme = useTheme()

  const getDescription = () => {
    switch (message.type) {
      case 'Message': {
        return stripHTML(message.body)
      }

      case 'PatientCommunication': {
        const lastMessage = message.messages.at(0)
        return lastMessage?.content ? stripHTML(lastMessage.content) : ''
      }

      default: {
        return ''
      }
    }
  }

  return (
    <FlexRow
      onClick={onClick}
      flex={1}
      gap={theme.UI.Spacing.Space4}
      justifyContent='space-between'
      alignItems='center'
      style={{
        width: '100%',
      }}
    >
      {/* Icon */}
      <FlexColumn flex={1}>
        <StMessageIcon>{messageIcons[message.type]}</StMessageIcon>
      </FlexColumn>

      {/* Display & Description */}
      <FlexColumn
        flex={1}
        gap={theme.UI.Spacing.Space1}
        justifyContent='center'
        alignItems='flex-start'
        flexGrow={1}
        style={{ overflow: 'hidden' }}
      >
        <FlexRow
          style={{
            width: '100%',
            justifyContent: 'space-between',
          }}
          gap={theme.UI.Spacing.Space4}
          alignItems='center'
        >
          <StMessageDisplay>{message.title}</StMessageDisplay>
          {message.readStatus === MessageReadStatus.Unread && <UnreadDot />}
        </FlexRow>
        <FlexRow
          style={{
            width: '100%',
            justifyContent: 'space-between',
          }}
          gap={theme.UI.Spacing.Space4}
        >
          <StMessageDescription>{getDescription()}</StMessageDescription>

          <StMessageDate>
            {message.date
              ? new Date(message.date).toLocaleDateString('nl-BE', {
                  month: 'long',
                  day: 'numeric',
                })
              : ''}
          </StMessageDate>
        </FlexRow>
      </FlexColumn>
    </FlexRow>
  )
}

export const StMessageIcon = styled(FlexColumn)`
  border-radius: 100%;

  height: 44px;
  width: 44px;

  align-items: center;
  justify-content: center;

  border: 1px solid ${theme.Colors.Neutrals[50]};
`

const StMessageDisplay = styled.p`
  font-size: ${theme.Typography.BodySmallBold['font-size']};
  line-height: ${theme.Typography.BodySmallBold['line-height']};
  color: ${theme.Colors.Neutrals.Darkest};

  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  font-weight: 700;

  margin: 0;
  padding: 0;
`

const StMessageDescription = styled.p`
  font-size: ${theme.Typography.BodySmallRegular['font-size']};
  line-height: ${theme.Typography.BodySmallRegular['line-height']};
  color: ${theme.Colors.Neutrals.Darker};
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;

  margin: 0;
  padding: 0;
`

const StMessageDate = styled.p`
  font-size: ${theme.Typography.BodySmallRegular['font-size']};
  line-height: ${theme.Typography.BodySmallRegular['line-height']};
  color: ${theme.Colors.Neutrals.Darker};
  margin: 0;
  padding: 0;
`

const UnreadDot = styled.div`
  ${({ theme }) => `
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${theme.Colors.Status.Error};
  `}
`
